import * as next from '../../../types/next'
import type {
  ContactControlOptions,
  LegacyPersonalInformationControlOptions
} from '../../../utils'
import { CUSTOMER_TYPE } from '../../../utils'
import { CONTROL_NAME } from '../../../utils/blocks/types'
import type { UpBlockHandler } from '../types'

import { upCommonSettings } from './common'

const upField = (field: any): next.CommonFieldSettings => {
  if (!field)
    return {
      display: false,
      required: false
    }

  return {
    display: true,
    required: field.required || false,
    ...(field.options && { options: field.options }),
    ...(field.default && { options: field.default })
  }
}

const upAllFields = (fields: Record<string, any>) => {
  return {
    salutation: upField(fields.salutation),
    title: upField(fields.title),
    firstName: upField(fields.firstName),
    lastName: upField(fields.lastName),
    email: upField(fields.email),
    telephone: upField(fields.telephone),
    birthDate: upField(fields.birthDate),
    companyName: upField(fields.companyName),
    registryCourt: upField(fields.registryCourt),
    registerNumber: upField(fields.registerNumber)
  } as next.PrivatePIBlockFields & next.BusinessPIBlockFields
}

const upPrivateFields = (
  fields: Record<string, any>
): next.PrivatePIBlockFields => {
  return {
    salutation: upField(fields.salutation),
    title: upField(fields.title),
    firstName: upField(fields.firstName),
    lastName: upField(fields.lastName),
    email: upField(fields.email),
    telephone: upField(fields.telephone),
    birthDate: upField(fields.birthDate)
  } as next.PrivatePIBlockFields
}

const upBusinessFields = (
  fields: Record<string, any>
): next.BusinessPIBlockFields & next.PrivatePIBlockFields => {
  return upAllFields(fields) as next.BusinessPIBlockFields &
    next.PrivatePIBlockFields
}

const upUserDefinedFields = (
  fields: Record<string, any>
): next.BusinessPIBlockFields & next.PrivatePIBlockFields => {
  return upAllFields(fields) as next.BusinessPIBlockFields &
    next.PrivatePIBlockFields
}

export const up: UpBlockHandler = (uischema) => {
  if (uischema.type !== CONTROL_NAME.PERSONAL_INFORMATION_CONTROL) {
    throw new Error('Expected a valid Personal Information control')
  }
  const optionsRaw = uischema.options || {}

  const options = mapToContactOptions(optionsRaw)

  if ('error' in options) {
    throw new Error(
      `Could not transform Personal Information Block to Contact options due to ${options.error}`
    )
  }

  const mode = getMode(options)
  let fields: next.PersonalInformationBlock['settings']['fields']

  switch (mode) {
    case next.PersonalInformationBlockMode.PRIVATE:
      fields = upPrivateFields(options.fields) as next.PrivatePIBlockFields
      break
    case next.PersonalInformationBlockMode.BUSINESS:
      fields = upBusinessFields(options.fields) as next.BusinessPIBlockFields &
        next.PrivatePIBlockFields
      break
    case next.PersonalInformationBlockMode.USER_DEFINED:
      fields = upUserDefinedFields(
        options.fields
      ) as next.BusinessPIBlockFields & next.PrivatePIBlockFields
      break
    default:
      throw new Error(`Invalid mode found in PI Block: ${mode}`)
  }

  return {
    type: next.BLOCK_TYPE.PERSONAL_INFORMATION,
    settings: {
      ...upCommonSettings(uischema),
      mode: mode,
      fields: fields,
      purpose: options.purposeLabels || []
    },
    references: {}
  }
}

const getMode = (options: ContactControlOptions) => {
  // The new block already contain the mode in the options
  if ('mode' in options) {
    const mode = options.mode

    if (!mode) {
      // If mode is defined but is null we follow the configurator logic and default to private
      return next.PersonalInformationBlockMode.PRIVATE
    }

    if (mode === 'private') {
      return next.PersonalInformationBlockMode.PRIVATE
    }

    if (mode === 'business') {
      return next.PersonalInformationBlockMode.BUSINESS
    }

    if (mode === 'both') {
      return next.PersonalInformationBlockMode.USER_DEFINED
    }

    throw new Error(`Invalid mode found in PI Block: ${mode}`)
  }

  // If the mode is not in the options, we need to infer it from the fields
  const fields = options.fields

  if (!fields) {
    throw new Error('PI block without fields is invalid')
  }

  if ('customerType' in fields) {
    return next.PersonalInformationBlockMode.USER_DEFINED
  }

  if ('companyName' in fields) {
    const companyNameField = fields.companyName ?? {}

    if ('showIf' in companyNameField) {
      return next.PersonalInformationBlockMode.USER_DEFINED
    }

    return next.PersonalInformationBlockMode.BUSINESS
  }

  return next.PersonalInformationBlockMode.PRIVATE
}

const isLegacyDto = (
  obj: any
): obj is LegacyPersonalInformationControlOptions<any> =>
  obj && obj['fieldsOrder']

export const mapToContactOptions = (
  dto: LegacyPersonalInformationControlOptions<any>
): ContactControlOptions | { error: string } => {
  const fields: ContactControlOptions['fields'] = {}

  if (!dto) return { error: 'Incoming DTO is not set' }

  // the following function is intercepting the flow to map the legacy dto to the new dto
  // it will also mark field as disabled if it is part of the disabledFields array
  if (!isLegacyDto(dto)) {
    return dto as ContactControlOptions
  }

  const fieldsDto =
    dto.fieldsOrder && Array.isArray(dto.fieldsOrder) ? dto.fieldsOrder : []

  try {
    fieldsDto.forEach((item) => {
      const fieldName = item.name

      if (!fieldName) throw 'DTO Mapping -> field name does not exist'

      let placeholder: string | undefined = undefined
      let showIf: { customerType: CUSTOMER_TYPE } | undefined = undefined
      let genderType: 'GENDER3' | undefined = undefined
      let defaultValue: string | undefined
      let options: { value: string; label: string }[] | undefined

      const label = dto.labels?.[fieldName]
      const errorMessage = dto.errorMessages?.[fieldName]
      const required = dto.requiredFields?.[fieldName]

      if (
        fieldName !== 'salutation' &&
        fieldName !== 'title' &&
        fieldName !== 'agreement'
      ) {
        placeholder = dto.placeHolders ? dto.placeHolders[fieldName] : undefined
      }

      if (
        fieldName === 'customerType' ||
        fieldName === 'companyName' ||
        fieldName === 'registryCourt' ||
        fieldName === 'registerNumber'
      ) {
        showIf =
          dto.showCondition?.[fieldName] &&
          dto.showCondition[fieldName] !== 'Always'
            ? { customerType: CUSTOMER_TYPE.BUSINESS }
            : undefined
      }

      if (
        fieldName === 'salutation' &&
        dto.salutationType === 'Mr/Mrs/Diverse'
      ) {
        genderType = 'GENDER3'
      }

      // check if there is a custom default selection, or custom labels
      if (fieldName === 'customerType') {
        if (dto.defaultCustomerTypeAndLabelOptions) {
          if (dto.defaultCustomerTypeAndLabelOptions.defaultCustomerSelection) {
            defaultValue =
              dto.defaultCustomerTypeAndLabelOptions.defaultCustomerSelection
          }
          if (dto.defaultCustomerTypeAndLabelOptions.customerTypeLabelOptions) {
            const { customerTypeLabelOptions } =
              dto.defaultCustomerTypeAndLabelOptions

            if (
              customerTypeLabelOptions.businessLabel ||
              customerTypeLabelOptions.privateLabel
            ) {
              options = [
                {
                  value: 'Private',
                  label: customerTypeLabelOptions.privateLabel || 'Private'
                },
                {
                  value: 'Business',
                  label: customerTypeLabelOptions.businessLabel || 'Business'
                }
              ]
            }
          }
        }
      }

      Object.assign(fields, {
        [fieldName]: {
          ...(label && { label }),
          ...(errorMessage && { errorMessage }),
          ...(placeholder && { placeholder }),
          ...(required && { required }),
          ...(showIf && { showIf }),
          ...(genderType && { genderType }),
          ...(defaultValue && { defaultValue }),
          ...(options && { options })
        }
      })
    })
  } catch (error) {
    // if any error in mapping, return it here
    if (error instanceof Error) {
      return { error: error.message }
    } else {
      return { error: `${error}` }
    }
  }

  return { fields }
}
